import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { Hokm, Role, HokmType } from '../enums';
import type { BinaryReader } from '../BinaryReader';
import { parseHand } from './blackjack';
import type { HokmGamePayload, SelectHokmPayload, SelectHakemPayload } from '../models/hokmGame';

/**
 * Hokm in-game codec. Mirrors HokmGame.cs (server) and Client/Assets/Code/HokmGame.cs (Unity reader),
 * verified against the server's NetDataWriter Put order. Field order below IS the wire order — never
 * reorder. Card strings are "-"-joined 2-char codes ("AS","TH","BB"); "" is an empty hand.
 */

// ---- C2S (encode) ----

/** SelectHokm(57): the hakem picks the trump — a single short (Hokm enum value 0–7). */
export function encodeSelectHokm(hokm: Hokm): Uint8Array {
  return beginMessage(MessageTag.SelectHokm).putShort(hokm).toUint8Array();
}

/** SelectCard(30): play one card — its 2-char code ("AS"). The new state arrives via GetGame. */
export function encodeSelectCard(code: string): Uint8Array {
  return beginMessage(MessageTag.SelectCard).putString(code).toUint8Array();
}

// ---- S2C (decode) ----

/** GetGame(28) / GamePreview(65): the full board — 10 hand strings, 3 shorts, time, 4 short counters. */
export function decodeHokmGetGame(r: BinaryReader): HokmGamePayload {
  return {
    available: parseHand(r.getString()),
    myHand: parseHand(r.getString()),
    mySub1: parseHand(r.getString()),
    mySub2: parseHand(r.getString()),
    mySub3: parseHand(r.getString()),
    oppHand: parseHand(r.getString()),
    oppSub1: parseHand(r.getString()),
    oppSub2: parseHand(r.getString()),
    oppSub3: parseHand(r.getString()),
    deck: parseHand(r.getString()),
    hokm: r.getShort() as Hokm,
    hakem: r.getShort() as Role,
    turn: r.getShort() as Role,
    turnNextTrigger: r.getInt(),
    player1TimeBank: r.getInt(),
    player2TimeBank: r.getInt(),
    player1Tricks: r.getShort(),
    player2Tricks: r.getShort(),
    player1HandsWon: r.getShort(),
    player2HandsWon: r.getShort(),
  };
}

/** SelectHokm(57): trump-pick phase — 2 hand strings, hakem/hokmType/turn, time, 4 short counters. */
export function decodeSelectHokm(r: BinaryReader): SelectHokmPayload {
  return {
    myHand: parseHand(r.getString()),
    oppHand: parseHand(r.getString()),
    hakem: r.getShort() as Role,
    hokmType: r.getShort() as HokmType,
    turn: r.getShort() as Role,
    turnNextTrigger: r.getInt(),
    player1TimeBank: r.getInt(),
    player2TimeBank: r.getInt(),
    player1Tricks: r.getShort(),
    player2Tricks: r.getShort(),
    player1HandsWon: r.getShort(),
    player2HandsWon: r.getShort(),
  };
}

/** SelectHakem(61): the hand-start reveal — both raw hand strings + the hakem role. */
export function decodeSelectHakem(r: BinaryReader): SelectHakemPayload {
  return {
    player1Hand: r.getString(),
    player2Hand: r.getString(),
    hakem: r.getShort() as Role,
  };
}

/** InGameMessage(53): a single transient string (per-hand win/loss notice or validation error). */
export function decodeInGameMessage(r: BinaryReader): string {
  return r.getString();
}

// ---- display helpers ----

/** The Unicode pip for a standard-suit trump, or null for the Naras/Saras variants. */
export function hokmGlyph(h: Hokm): string | null {
  switch (h) {
    case Hokm.Clubs:
      return '♣';
    case Hokm.Diamonds:
      return '♦';
    case Hokm.Hearts:
      return '♥';
    case Hokm.Spades:
      return '♠';
    default:
      return null;
  }
}

/** Diamonds/Hearts render red; Clubs/Spades render dark. */
export function hokmIsRed(h: Hokm): boolean {
  return h === Hokm.Diamonds || h === Hokm.Hearts;
}

/** Persian label for a trump (suit name or Naras/Saras variant), or "—" when unset. */
export function hokmLabelFa(h: Hokm): string {
  switch (h) {
    case Hokm.Clubs:
      return 'گشنیز';
    case Hokm.Diamonds:
      return 'خشت';
    case Hokm.Hearts:
      return 'دل';
    case Hokm.Spades:
      return 'پیک';
    case Hokm.Naras:
      return 'ناراس';
    case Hokm.Saras:
      return 'ساراس';
    case Hokm.AsNaras:
      return 'آس ناراس';
    case Hokm.AsSaras:
      return 'آس ساراس';
    default:
      return '—';
  }
}

/** The trump options a hakem may pick, given the table's HokmType (4 suits, or 8 with Naras/Saras). */
export function hokmOptions(type: HokmType): Hokm[] {
  const suits = [Hokm.Clubs, Hokm.Diamonds, Hokm.Hearts, Hokm.Spades];
  if (type === HokmType.WithNaras) {
    return [...suits, Hokm.Naras, Hokm.Saras, Hokm.AsNaras, Hokm.AsSaras];
  }
  return suits;
}
